'use client';

import {
    AirportShuttleRounded as AirportShuttleIcon,
    CategoryRounded as CategoryIcon,
    ConfirmationNumberRounded as ActivityIcon,
    DirectionsBoatRounded as CruiseIcon,
    DirectionsCarRounded as CarIcon,
    FlightRounded as FlightIcon,
    HotelRounded as HotelIcon,
    RestaurantRounded as RestaurantIcon,
    RouteRounded as ItineraryIcon,
} from '@mui/icons-material';
import { Box, Button, ToggleButton } from '@mui/material';
import { radius } from '@odys/design-system/tokens/radius';
import { useTranslations } from 'next-intl';

import type { DmcCategoryCountsDto } from '@/app/api/lib/dmcs/dmc.types';
import type { ProductCategory } from '@/app/api/lib/products/product.types';
import { PRODUCT_CATEGORIES } from '@/app/api/lib/products/product.vocabulary';
import { Link } from '@/configurations/i18n/navigation';
import { trackNavigationClick } from '@/configurations/tracking/navigation';

import type React from 'react';

type CategoryKey = ProductCategory;

const CATEGORY_ICONS: Record<CategoryKey, React.ReactNode> = {
    hotels: <HotelIcon fontSize="small" />,
    activities: <ActivityIcon fontSize="small" />,
    cruises: <CruiseIcon fontSize="small" />,
    transports: <AirportShuttleIcon fontSize="small" />,
    internationalFlight: <FlightIcon fontSize="small" />,
    rentalCars: <CarIcon fontSize="small" />,
    restaurants: <RestaurantIcon fontSize="small" />,
    general: <CategoryIcon fontSize="small" />,
};

/**
 * État courant de la barre :
 *  - `category` (page produits) : une catégorie est active ; cliquer une autre
 *    catégorie ne navigue pas, on intercepte pour piloter le state local + URL
 *    (`onSwitch`). Le bouton Itinéraires reste un CTA de navigation.
 *  - `itineraries` (page itinéraires) : le bouton Itinéraires est actif ; les
 *    catégories deviennent de vrais liens vers la page produits (pas de state
 *    local à piloter ici).
 */
export type CategorySwitcherActive =
    | { kind: 'category'; category: CategoryKey; onSwitch: (next: CategoryKey) => void }
    | { kind: 'itineraries' };

/**
 * Barre de navigation Catalogue partagée entre la page produits et la page
 * itinéraires : les catégories (DMC ayant ≥ 1 produit) + un bouton Itinéraires.
 * Le bouton correspondant à la page courante est rendu dans l'état actif.
 */
export function CategorySwitcher({
    categoryCounts,
    dmcSlug,
    hasItineraries,
    active,
}: {
    categoryCounts: DmcCategoryCountsDto;
    dmcSlug: string;
    hasItineraries: boolean;
    active: CategorySwitcherActive;
}) {
    const t = useTranslations('dmcProductsPage');
    // Seules les catégories dans lesquelles la DMC a au moins un Produit sont
    // rendues (cf. spec dmc-products) : un bouton vide n'apparaît pas. On rend
    // des ToggleButton autonomes dans un Box flex plutôt qu'un ToggleButtonGroup
    // pour garder le contrôle du markup (chaque bouton est un Link autonome).
    const visibleKeys = PRODUCT_CATEGORIES.filter((key) => categoryCounts[key] > 0);
    const isItinerariesActive = active.kind === 'itineraries';
    const showItineraries = hasItineraries || isItinerariesActive;

    return (
        <Box
            role="group"
            data-testid="dmc-products-category-switcher"
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
        >
            {visibleKeys.map((key) => {
                const isActive = active.kind === 'category' && key === active.category;
                const href = `/dmcs/${dmcSlug}/products?category=${key}`;
                return (
                    <ToggleButton
                        key={key}
                        value={key}
                        selected={isActive}
                        component={Link}
                        href={href}
                        onClick={(e: React.MouseEvent<HTMLElement>) => {
                            if (!isActive) {
                                trackNavigationClick({
                                    navItem: 'category_tab',
                                    triggerContext: 'category_switcher',
                                    productCategory: key,
                                });
                            }
                            // Right-click / cmd+click / ctrl+click → laisse le browser ouvrir
                            // l'URL canonique. Click simple → on intercepte pour gérer le state
                            // local + URL sync, sans navigation. En mode `itineraries` on ne
                            // l'intercepte pas : le ToggleButton se comporte en simple Link.
                            if (
                                active.kind !== 'category' ||
                                e.metaKey ||
                                e.ctrlKey ||
                                e.shiftKey ||
                                e.altKey ||
                                e.button !== 0
                            ) {
                                return;
                            }
                            e.preventDefault();
                            active.onSwitch(key);
                        }}
                        data-testid={`dmc-products-category-${key}`}
                        aria-current={isActive ? 'page' : undefined}
                        sx={{
                            gap: 1,
                            px: 2.5,
                            py: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: radius.sm,
                            textTransform: 'none',
                            fontWeight: 500,
                            bgcolor: 'background.paper',
                            color: 'text.primary',
                            '& .MuiSvgIcon-root': { color: 'primary.main' },
                            '&.Mui-selected': {
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                                '& .MuiSvgIcon-root': { color: 'primary.contrastText' },
                                '&:hover': { bgcolor: 'primary.dark' },
                            },
                        }}
                    >
                        {CATEGORY_ICONS[key]}
                        {t(`categoryLabels.${key}`)}
                    </ToggleButton>
                );
            })}
            {showItineraries && (
                <Button
                    component={Link}
                    href={`/dmcs/${dmcSlug}/itineraries`}
                    variant={isItinerariesActive ? 'contained' : 'outlined'}
                    color="primary"
                    startIcon={<ItineraryIcon fontSize="small" />}
                    data-testid="dmc-products-itineraries-cta"
                    aria-current={isItinerariesActive ? 'page' : undefined}
                    sx={{
                        ml: 'auto',
                        px: 2.5,
                        py: 1,
                        borderRadius: radius.md,
                        textTransform: 'none',
                        fontWeight: 500,
                        ...(isItinerariesActive ? {} : { bgcolor: 'background.paper' }),
                    }}
                >
                    {t('itinerariesCta')}
                </Button>
            )}
        </Box>
    );
}
